import { computed, Injectable, signal } from '@angular/core';
import { RichTextValue } from '../pbir/RichTextValue';
import { CustomFields } from '../pbir/visuals/customFields/CustomFields';
import { AppCurrenciesDto, AppSettingsDto, AppUserDto, CustomFieldFunction, EnterpriseUserDataDto, ListItemCategoryDto, ListVersionedProductDto, LocaleDto, UserAppFeaturesDto } from '../services/api.services';
import { deepClone } from '../shared/clone/cloner-service';
import { getTimeZoneById } from '../shared/utils';
import { AppCurrencies } from '../state/app/app.reducer';
import { AppUserWrapper } from './user';

@Injectable({
  providedIn: 'root'
})
export class AppContext {
  readonly user = signal<AppUserWrapper>(null);
  readonly organisationId = computed(() => this.user()?.organisationId);
  readonly enterpriseUserData = signal<EnterpriseUserDataDto>(null);

  readonly features = signal<UserAppFeaturesDto>(null);
  readonly settings = signal<AppSettingsDto>(null);

  readonly locales = signal<LocaleDto[]>(null);
  readonly localeId = computed(() => this.settings()?.locale);
  readonly locale = computed(() => {
    let id = this.localeId();
    if (!id)
      return null;

    return this.locales()?.find(l => l.id == id);
  });

  findLocaleById(id: string) {
    id = id?.toLowerCase();
    return this.locales()?.find(x => x.id.toLowerCase() == id);
  }


  readonly timeZoneIana = computed(() => this.settings()?.timeZoneIana);
  readonly timeZone = computed(() => {
    let id = this.timeZoneIana();
    if (!id)
      return null;

    return getTimeZoneById(id);
  });


  readonly customFieldFunctions = signal<ReadonlyArray<CustomFields.Function>>([]);

  readonly products = signal<ListVersionedProductDto[]>(null);
  readonly itemCategories = signal<ListItemCategoryDto[]>(null);

  init(data: {
    user: AppUserDto,
    enterpriseUserData: EnterpriseUserDataDto,
    features: UserAppFeaturesDto,
    settings: AppSettingsDto,
    locales: LocaleDto[],
    customFieldFunctions: CustomFieldFunction[],
    products: ListVersionedProductDto[],
    itemCategories: ListItemCategoryDto[],
  }) {
    this.user.set(new AppUserWrapper(deepClone(data.user)));
    this.enterpriseUserData.set(data.enterpriseUserData);

    this.features.set(deepClone(data.features));
    this.settings.set(deepClone(data.settings));

    let locales = data.locales?.map(l => ({
      ...l,
      formats: deepClone(l.formats),
      numberFormatExample: getLocaleNumberFormatExample(l)
    }));

    this.locales.set(locales);

    this.customFieldFunctions.set(data.customFieldFunctions.map(f => ({
      ...f,
      descriptionValue: RichTextValue.markdown(f.description?.join('\r\n') ?? ''),
    })));

    this.products.set(data.products);
    this.itemCategories.set(data.itemCategories);
  }
}

function getLocaleNumberFormatExample(l: LocaleDto) {
  let intl = new Intl.NumberFormat(l.id, {
    maximumFractionDigits: 2,
  });

  let format = intl.format(-9999.99);

  return format.replace(/9/g, '#');
}
